/**
 * Bank rules — `/finance/bank-rules`.
 *
 * What a statement line does before anyone looks at it is decided here, by two
 * rule sets that run at different moments and are therefore shown apart rather
 * than merged into one list of "rules".
 *
 * Auto-posting rules run FIRST, in `sequence` order, and the first match wins.
 * They exist for the lines no document was ever raised for — bank charges,
 * credit interest, FX commission — so those post instead of queueing for a
 * human who would approve the same twelve fees every month. Because the first
 * match wins, the order is the rule: the list is shown in sequence order and
 * the sequence is never hidden.
 *
 * Reference rules run during matching, in `priority` order, and only pull a
 * document reference out of free-text narrative. They decide what a line is
 * matched AGAINST, never what it posts to.
 *
 * Two refusals are the screen's own, not the server's, because a user who is
 * told why can fix it: a pattern that does not compile, and an auto-posting
 * rule that constrains nothing and would therefore swallow the statement.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Combobox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { newIdempotencyKey } from "@/features/shared/ops-api";
import { useAccounts } from "@/features/ledger/queries";

import { useBankAccountList, useTaxCodeList } from "../queries";
import {
  DIRECTIONS,
  DOCUMENT_TYPES,
  createBankPostingRule,
  createBankReferenceRule,
  deleteBankPostingRule,
  deleteBankReferenceRule,
  listBankPostingRules,
  listBankReferenceRules,
  patternCompiles,
  postingRuleConstrains,
  replaceBankPostingRule,
  replaceBankReferenceRule,
  type BankDocumentType,
  type BankPostingRule,
  type BankPostingRuleWrite,
  type BankReferenceRule,
  type BankReferenceRuleWrite,
} from "../bank-rules-api";

const REFERENCE_KEY = ["finance/bank-reference-rules"];
const POSTING_KEY = ["finance/bank-posting-rules"];

export interface BankRulesPageProps {
  canConfigure?: boolean;
}

export function BankRulesPage({ canConfigure = false }: BankRulesPageProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [referenceDraft, setReferenceDraft] = useState<BankReferenceRule | null | undefined>(
    undefined,
  );
  const [postingDraft, setPostingDraft] = useState<BankPostingRule | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  const references = useQuery({
    queryKey: REFERENCE_KEY,
    queryFn: async ({ signal }) => [...(await listBankReferenceRules(signal)).items],
  });
  const postings = useQuery({
    queryKey: POSTING_KEY,
    queryFn: async ({ signal }) => [...(await listBankPostingRules(signal)).items],
  });

  const accounts = useAccounts({ is_postable: true, active: true });
  const taxCodes = useTaxCodeList({ active: true }, 200);
  const bankAccounts = useBankAccountList({}, 200);

  const accountLabel = (accountId: string): string => {
    const account = (accounts.data ?? []).find((candidate) => candidate.id === accountId);
    return account === undefined ? accountId : `${account.code} · ${account.name}`;
  };
  const bankLabel = (bankAccountId: string | null): string => {
    if (bankAccountId === null) return t("finance.bankRules.everyAccount", "Every bank account");
    const bank = bankAccounts.rows.find((candidate) => candidate.id === bankAccountId);
    return bank === undefined
      ? t("finance.bankRules.otherAccount", "Another bank account")
      : bank.name;
  };

  const remove = (kind: "reference" | "posting", ruleId: string) => {
    setError(null);
    const request =
      kind === "reference" ? deleteBankReferenceRule(ruleId) : deleteBankPostingRule(ruleId);
    void request
      .then(() =>
        queryClient.invalidateQueries({
          queryKey: kind === "reference" ? REFERENCE_KEY : POSTING_KEY,
        }),
      )
      .catch(() => setError(t("finance.bankRules.deleteError", "The rule could not be deleted.")));
  };

  const sortedPostings = useMemo(
    () => [...(postings.data ?? [])].sort((left, right) => left.sequence - right.sequence),
    [postings.data],
  );
  const sortedReferences = useMemo(
    () => [...(references.data ?? [])].sort((left, right) => left.priority - right.priority),
    [references.data],
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight">
          {t("finance.bankRules.title", "Bank rules")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t("finance.bankRules.subtitle", "What a statement line does before anyone looks at it.")}
        </p>
      </div>

      {error === null ? null : (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("finance.bankRules.postingTitle", "Auto-posting rules")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t(
              "finance.bankRules.postingHint",
              "These run before matching, in sequence order, and the first match wins. They are for the lines no document was ever raised for — charges, interest, commission.",
            )}
          </p>
          {postings.isLoading ? (
            <p className="text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
          ) : sortedPostings.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("finance.bankRules.noPosting", "No auto-posting rule is defined.")}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[52rem] text-sm">
                <thead>
                  <tr className="border-b text-xs uppercase text-muted-foreground">
                    <th scope="col" className="p-2 text-start">
                      {t("finance.bankRules.sequence", "Order")}
                    </th>
                    <th scope="col" className="p-2 text-start">
                      {t("finance.bankRules.rule", "Rule")}
                    </th>
                    <th scope="col" className="p-2 text-start">
                      {t("finance.bankRules.matches", "Matches")}
                    </th>
                    <th scope="col" className="p-2 text-start">
                      {t("finance.bankRules.postsTo", "Posts to")}
                    </th>
                    {canConfigure ? <th scope="col" className="p-2" /> : null}
                  </tr>
                </thead>
                <tbody>
                  {sortedPostings.map((rule) => (
                    <tr key={rule.id} className="border-b last:border-0">
                      <td className="p-2 tabular-nums" dir="ltr">
                        {rule.sequence}
                      </td>
                      <td className="p-2">
                        <span className="block">{rule.name}</span>
                        <span className="block text-xs text-muted-foreground">
                          {bankLabel(rule.bank_account_id)}
                        </span>
                      </td>
                      <td className="p-2 text-xs">
                        <Badge variant="secondary">
                          {t(`finance.bankDirection.${rule.direction}`, rule.direction)}
                        </Badge>
                        {rule.counterparty_pattern === undefined ||
                        rule.counterparty_pattern === "" ? null : (
                          <span dir="ltr" className="ms-2 font-mono">
                            {rule.counterparty_pattern}
                          </span>
                        )}
                        {rule.reference_pattern === undefined ||
                        rule.reference_pattern === "" ? null : (
                          <span dir="ltr" className="ms-2 font-mono">
                            {rule.reference_pattern}
                          </span>
                        )}
                        {rule.min_amount === null && rule.max_amount === null ? null : (
                          <span dir="ltr" className="ms-2 tabular-nums">
                            {rule.min_amount ?? "…"} – {rule.max_amount ?? "…"}
                          </span>
                        )}
                      </td>
                      <td className="p-2">
                        <span className="block">{accountLabel(rule.gl_account_id)}</span>
                        {rule.active ? null : (
                          <Badge variant="outline">
                            {t("finance.bankRules.inactive", "Inactive")}
                          </Badge>
                        )}
                      </td>
                      {canConfigure ? (
                        <td className="p-2 text-end">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={t("actions.edit", "Edit")}
                            onClick={() => setPostingDraft(rule)}
                          >
                            <Pencil className="size-4" aria-hidden="true" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={t("actions.delete", "Delete")}
                            onClick={() => remove("posting", rule.id)}
                          >
                            <Trash2 className="size-4" aria-hidden="true" />
                          </Button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {canConfigure ? (
            <Button type="button" variant="outline" onClick={() => setPostingDraft(null)}>
              <Plus className="size-4" aria-hidden="true" />
              {t("finance.bankRules.newPosting", "New auto-posting rule")}
            </Button>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("finance.bankRules.referenceTitle", "Reference rules")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t(
              "finance.bankRules.referenceHint",
              "These pull a document reference out of free-text narrative so matching has something to match on. They decide what a line is matched against, never what it posts to.",
            )}
          </p>
          {references.isLoading ? (
            <p className="text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
          ) : sortedReferences.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("finance.bankRules.noReference", "No reference rule is defined.")}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[44rem] text-sm">
                <thead>
                  <tr className="border-b text-xs uppercase text-muted-foreground">
                    <th scope="col" className="p-2 text-start">
                      {t("finance.bankRules.priority", "Priority")}
                    </th>
                    <th scope="col" className="p-2 text-start">
                      {t("finance.bankRules.rule", "Rule")}
                    </th>
                    <th scope="col" className="p-2 text-start">
                      {t("finance.bankRules.pattern", "Pattern")}
                    </th>
                    <th scope="col" className="p-2 text-start">
                      {t("finance.bankRules.target", "Points at")}
                    </th>
                    {canConfigure ? <th scope="col" className="p-2" /> : null}
                  </tr>
                </thead>
                <tbody>
                  {sortedReferences.map((rule) => (
                    <tr key={rule.id} className="border-b last:border-0">
                      <td className="p-2 tabular-nums" dir="ltr">
                        {rule.priority}
                      </td>
                      <td className="p-2">
                        <span className="block">{rule.name}</span>
                        {rule.active ? null : (
                          <Badge variant="outline">
                            {t("finance.bankRules.inactive", "Inactive")}
                          </Badge>
                        )}
                      </td>
                      <td className="p-2">
                        <code dir="ltr" className="block font-mono text-xs">
                          {rule.pattern}
                        </code>
                        <span className="text-xs text-muted-foreground">
                          {t("finance.bankRules.captureGroup", "Group {{group}}", {
                            group: rule.capture_group,
                          })}
                        </span>
                      </td>
                      <td className="p-2">
                        {t(`finance.bankDocumentType.${rule.target}`, rule.target)}
                      </td>
                      {canConfigure ? (
                        <td className="p-2 text-end">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={t("actions.edit", "Edit")}
                            onClick={() => setReferenceDraft(rule)}
                          >
                            <Pencil className="size-4" aria-hidden="true" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={t("actions.delete", "Delete")}
                            onClick={() => remove("reference", rule.id)}
                          >
                            <Trash2 className="size-4" aria-hidden="true" />
                          </Button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {canConfigure ? (
            <Button type="button" variant="outline" onClick={() => setReferenceDraft(null)}>
              <Plus className="size-4" aria-hidden="true" />
              {t("finance.bankRules.newReference", "New reference rule")}
            </Button>
          ) : null}
        </CardContent>
      </Card>

      {referenceDraft === undefined ? null : (
        <ReferenceRuleDialog
          rule={referenceDraft}
          onClose={() => setReferenceDraft(undefined)}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: REFERENCE_KEY });
          }}
        />
      )}
      {postingDraft === undefined ? null : (
        <PostingRuleDialog
          rule={postingDraft}
          accountOptions={(accounts.data ?? []).map((account) => ({
            value: account.id,
            label: `${account.code} · ${account.name}`,
          }))}
          taxCodeOptions={taxCodes.rows.map((code) => ({
            value: code.id,
            label: `${code.code} · ${code.name}`,
          }))}
          bankOptions={bankAccounts.rows.map((bank) => ({ value: bank.id, label: bank.name }))}
          onClose={() => setPostingDraft(undefined)}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: POSTING_KEY });
          }}
        />
      )}
    </div>
  );
}

// ── Reference rule form ──────────────────────────────────────────────────

function ReferenceRuleDialog({
  rule,
  onClose,
  onSaved,
}: {
  rule: BankReferenceRule | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: rule?.name ?? "",
    priority: String(rule?.priority ?? 100),
    pattern: rule?.pattern ?? "",
    capture_group: String(rule?.capture_group ?? 1),
    target: rule?.target ?? ("customer_receipt" as BankDocumentType),
    active: rule?.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const compiles = form.pattern === "" || patternCompiles(form.pattern);
  const complete = form.name.trim() !== "" && form.pattern !== "" && compiles;

  const save = () => {
    if (!complete) return;
    setBusy(true);
    setError(null);
    const body: BankReferenceRuleWrite = {
      name: form.name.trim(),
      priority: Number(form.priority),
      pattern: form.pattern,
      capture_group: Number(form.capture_group),
      target: form.target,
      active: form.active,
    };
    const request =
      rule === null
        ? createBankReferenceRule(body, newIdempotencyKey())
        : replaceBankReferenceRule(rule.id, body);
    void request
      .then(() => {
        onSaved();
        onClose();
      })
      .catch(() => setError(t("finance.bankRules.saveError", "The rule could not be saved.")))
      .finally(() => setBusy(false));
  };

  return (
    <Dialog open onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {rule === null
              ? t("finance.bankRules.newReference", "New reference rule")
              : t("finance.bankRules.editReference", "Edit reference rule")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "finance.bankRules.referenceFormHint",
              "The lowest priority runs first. The capture group is the part of the match that is the reference; 0 means the whole match.",
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="ref-name">{t("finance.bankRules.name", "Name")}</Label>
            <Input
              id="ref-name"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ref-priority">{t("finance.bankRules.priority", "Priority")}</Label>
            <Input
              id="ref-priority"
              type="number"
              min={1}
              max={999}
              dir="ltr"
              value={form.priority}
              onChange={(event) => setForm({ ...form, priority: event.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ref-group">{t("finance.bankRules.group", "Capture group")}</Label>
            <Input
              id="ref-group"
              type="number"
              min={0}
              max={9}
              dir="ltr"
              value={form.capture_group}
              onChange={(event) => setForm({ ...form, capture_group: event.target.value })}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="ref-pattern">{t("finance.bankRules.pattern", "Pattern")}</Label>
            <Input
              id="ref-pattern"
              dir="ltr"
              className="font-mono"
              value={form.pattern}
              onChange={(event) => setForm({ ...form, pattern: event.target.value })}
            />
            {compiles ? null : (
              <p className="text-sm text-destructive">
                {t(
                  "finance.bankRules.badPattern",
                  "This pattern does not compile. A rule that does not compile is skipped silently, so it is refused here instead.",
                )}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ref-target">{t("finance.bankRules.target", "Points at")}</Label>
            <Select
              value={form.target}
              onValueChange={(value) =>
                setForm({ ...form, target: value as BankReferenceRuleWrite["target"] })
              }
            >
              <SelectTrigger id="ref-target">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOCUMENT_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {t(`finance.bankDocumentType.${type}`, type)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end pb-2">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={form.active}
                onCheckedChange={(checked) => setForm({ ...form, active: checked === true })}
              />
              {t("finance.bankRules.active", "Active")}
            </label>
          </div>
        </div>

        {error === null ? null : (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t("actions.cancel", "Cancel")}
          </Button>
          <Button type="button" disabled={!complete || busy} onClick={save}>
            {t("actions.save", "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Auto-posting rule form ───────────────────────────────────────────────

interface Option {
  value: string;
  label: string;
}

function PostingRuleDialog({
  rule,
  accountOptions,
  taxCodeOptions,
  bankOptions,
  onClose,
  onSaved,
}: {
  rule: BankPostingRule | null;
  accountOptions: Option[];
  taxCodeOptions: Option[];
  bankOptions: Option[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    bank_account_id: rule?.bank_account_id ?? null,
    sequence: String(rule?.sequence ?? 100),
    name: rule?.name ?? "",
    counterparty_pattern: rule?.counterparty_pattern ?? "",
    reference_pattern: rule?.reference_pattern ?? "",
    direction: rule?.direction ?? "any",
    min_amount: rule?.min_amount ?? "",
    max_amount: rule?.max_amount ?? "",
    gl_account_id: rule?.gl_account_id ?? null,
    tax_code_id: rule?.tax_code_id ?? null,
    active: rule?.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const patternsCompile =
    (form.counterparty_pattern === "" || patternCompiles(form.counterparty_pattern)) &&
    (form.reference_pattern === "" || patternCompiles(form.reference_pattern));
  const constrains = postingRuleConstrains(form);
  const complete =
    form.name.trim() !== "" && form.gl_account_id !== null && patternsCompile && constrains;

  const save = () => {
    if (!complete) return;
    setBusy(true);
    setError(null);
    const body: BankPostingRuleWrite = {
      sequence: Number(form.sequence),
      name: form.name.trim(),
      direction: form.direction,
      gl_account_id: form.gl_account_id as string,
      active: form.active,
      // Every optional member is omitted rather than sent empty: an empty
      // pattern is not the same as no pattern to the matcher.
      ...(form.bank_account_id === null ? {} : { bank_account_id: form.bank_account_id }),
      ...(form.counterparty_pattern === ""
        ? {}
        : { counterparty_pattern: form.counterparty_pattern }),
      ...(form.reference_pattern === "" ? {} : { reference_pattern: form.reference_pattern }),
      ...(form.min_amount.trim() === "" ? {} : { min_amount: form.min_amount.trim() }),
      ...(form.max_amount.trim() === "" ? {} : { max_amount: form.max_amount.trim() }),
      ...(form.tax_code_id === null ? {} : { tax_code_id: form.tax_code_id }),
    };
    const request =
      rule === null
        ? createBankPostingRule(body, newIdempotencyKey())
        : replaceBankPostingRule(rule.id, body);
    void request
      .then(() => {
        onSaved();
        onClose();
      })
      .catch(() => setError(t("finance.bankRules.saveError", "The rule could not be saved.")))
      .finally(() => setBusy(false));
  };

  return (
    <Dialog open onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {rule === null
              ? t("finance.bankRules.newPosting", "New auto-posting rule")
              : t("finance.bankRules.editPosting", "Edit auto-posting rule")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "finance.bankRules.postingFormHint",
              "Rules are tried in order and the first match wins. The amount bounds are absolute values, so one rule covers a fee whether it is charged or refunded.",
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="post-name">{t("finance.bankRules.name", "Name")}</Label>
            <Input
              id="post-name"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="post-sequence">{t("finance.bankRules.sequence", "Order")}</Label>
            <Input
              id="post-sequence"
              type="number"
              min={1}
              max={999}
              dir="ltr"
              value={form.sequence}
              onChange={(event) => setForm({ ...form, sequence: event.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="post-direction">{t("finance.bankRules.direction", "Direction")}</Label>
            <Select
              value={form.direction}
              onValueChange={(value) =>
                setForm({ ...form, direction: value as BankPostingRuleWrite["direction"] })
              }
            >
              <SelectTrigger id="post-direction">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DIRECTIONS.map((direction) => (
                  <SelectItem key={direction} value={direction}>
                    {t(`finance.bankDirection.${direction}`, direction)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="post-bank">{t("finance.bankRules.bankAccount", "Bank account")}</Label>
            <Combobox
              id="post-bank"
              options={bankOptions}
              value={form.bank_account_id}
              onChange={(value) => setForm({ ...form, bank_account_id: value })}
              placeholder={t("finance.bankRules.everyAccount", "Every bank account")}
              searchPlaceholder={t("actions.search", "Search")}
              emptyMessage={t("finance.bankRules.noBankAccounts", "No bank account found.")}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="post-counterparty">
              {t("finance.bankRules.counterparty", "Counterparty pattern")}
            </Label>
            <Input
              id="post-counterparty"
              dir="ltr"
              className="font-mono"
              value={form.counterparty_pattern}
              onChange={(event) => setForm({ ...form, counterparty_pattern: event.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="post-reference">
              {t("finance.bankRules.referencePattern", "Reference pattern")}
            </Label>
            <Input
              id="post-reference"
              dir="ltr"
              className="font-mono"
              value={form.reference_pattern}
              onChange={(event) => setForm({ ...form, reference_pattern: event.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="post-min">{t("finance.bankRules.minAmount", "Least")}</Label>
            <Input
              id="post-min"
              dir="ltr"
              inputMode="decimal"
              className="text-end font-mono"
              value={form.min_amount}
              onChange={(event) => setForm({ ...form, min_amount: event.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="post-max">{t("finance.bankRules.maxAmount", "Most")}</Label>
            <Input
              id="post-max"
              dir="ltr"
              inputMode="decimal"
              className="text-end font-mono"
              value={form.max_amount}
              onChange={(event) => setForm({ ...form, max_amount: event.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="post-account">{t("finance.bankRules.postsTo", "Posts to")}</Label>
            <Combobox
              id="post-account"
              options={accountOptions}
              value={form.gl_account_id}
              onChange={(value) => setForm({ ...form, gl_account_id: value })}
              placeholder={t("finance.bankRules.pickAccount", "Account…")}
              searchPlaceholder={t("actions.search", "Search")}
              emptyMessage={t("finance.bankRules.noAccounts", "No account matches.")}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="post-tax">{t("finance.bankRules.taxCode", "Tax code")}</Label>
            <Combobox
              id="post-tax"
              options={taxCodeOptions}
              value={form.tax_code_id}
              onChange={(value) => setForm({ ...form, tax_code_id: value })}
              placeholder={t("finance.bankRules.noTax", "No tax")}
              searchPlaceholder={t("actions.search", "Search")}
              emptyMessage={t("finance.bankRules.noTaxCodes", "No tax code found.")}
            />
          </div>
          <div className="flex items-end pb-2">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={form.active}
                onCheckedChange={(checked) => setForm({ ...form, active: checked === true })}
              />
              {t("finance.bankRules.active", "Active")}
            </label>
          </div>
        </div>

        {patternsCompile ? null : (
          <p className="text-sm text-destructive">
            {t(
              "finance.bankRules.badPattern",
              "This pattern does not compile. A rule that does not compile is skipped silently, so it is refused here instead.",
            )}
          </p>
        )}
        {constrains ? null : (
          <p className="text-sm text-destructive">
            {t(
              "finance.bankRules.unconstrained",
              "Name a counterparty, a reference, or an amount bound. A rule that constrains nothing would post every line on the statement.",
            )}
          </p>
        )}
        {error === null ? null : (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t("actions.cancel", "Cancel")}
          </Button>
          <Button type="button" disabled={!complete || busy} onClick={save}>
            {t("actions.save", "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
